/**
 * Which build this is, and whether it is the current one.
 *
 * Answering "am I on the updated app?" from inside the app matters more here
 * than it looks. The service worker caches the shell, deploys reach edge nodes
 * at different times, and a phone that has been in a pocket since Tuesday may
 * be several builds behind — so "it should be fixed" and "your phone has the
 * fix" are genuinely different claims, and until now neither could be checked.
 *
 * The comparison needs no server support. Every build emits a content-hashed
 * bundle whose name changes whenever the code does, and the shell HTML names
 * it — so fetching the shell and reading that filename says what the server is
 * serving right now, and the page already knows which one it loaded.
 */

declare const __BUILD_ID__: string
declare const __BUILT_AT__: string

/** Short commit of the build this page is running. */
export const BUILD_ID: string = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev'

/** When it was built, ISO. */
export const BUILT_AT: string = typeof __BUILT_AT__ === 'string' ? __BUILT_AT__ : ''

/** "12 Aug, 09:14" — short enough to sit at the bottom of a settings list. */
export function buildLabel(): string {
  if (!BUILT_AT) return BUILD_ID
  const d = new Date(BUILT_AT)
  return `${BUILD_ID} · ${d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}, ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
}

/** The hashed bundle filename this page actually loaded. */
function loadedBundle(): string | null {
  const scripts = [...document.querySelectorAll('script[src]')] as HTMLScriptElement[]
  const found = scripts.map((s) => s.src.split('/').pop() ?? '').find((n) => n.startsWith('index-'))
  return found ?? null
}

export type UpdateState = 'current' | 'stale' | 'unknown'

/**
 * Ask the server what it is serving, and compare.
 *
 * This button reported "up to date" on a phone several builds behind, always.
 *
 * `cache: 'reload'` was doing half the job it looked like it was doing: it
 * bypasses the BROWSER's HTTP cache and has no authority over the service
 * worker, which gets the request first. And a bare `fetch('/')` from a page is
 * not a navigation — `mode` is `cors`, not `navigate`, and the browser sends
 * `Accept: * /*` rather than `text/html` — so it failed both arms of the
 * worker's `isNavigation` test, fell through to the cache-first branch, and was
 * answered from the very cached shell it existed to see past. The check compared
 * the running bundle against a copy of itself and could only ever agree.
 *
 * So the request is made unmistakable in two independent ways, because either
 * one alone leaves a hole:
 *
 *   `?__fresh=` is a URL the caches have never heard of, so a cache lookup
 *   misses even under the OLD worker still installed on somebody's phone — which
 *   matters, since a worker only updates on its own schedule and this fix has to
 *   work before that happens. The current worker also skips these outright.
 *
 *   `accept: text/html` is what the request always should have said, and it puts
 *   the shell request on the network-first path where it belongs.
 */
export async function checkForUpdate(): Promise<UpdateState> {
  const mine = loadedBundle()
  if (!mine) return 'unknown'
  try {
    const html = await fetch(`/?__fresh=${Date.now()}`, {
      cache: 'reload',
      headers: { accept: 'text/html' },
    }).then((r) => r.text())
    const theirs = html.match(/index-[A-Za-z0-9_-]+\.js/)?.[0]
    if (!theirs) return 'unknown'
    return theirs === mine ? 'current' : 'stale'
  } catch {
    // Offline is not out of date; it is unknown, and saying otherwise would
    // send somebody hunting for an update they cannot download anyway.
    return 'unknown'
  }
}

/**
 * Take the new build.
 *
 * The waiting service worker is told to step aside first — otherwise a reload
 * can be served the old shell by the worker that is still in charge, and the
 * app comes back on exactly the version the user was trying to leave.
 */
export async function applyUpdate(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker?.getRegistration()
    await reg?.update()
    if (reg?.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' })
    /*
     * The caches are NOT emptied here.
     *
     * This deleted every cache and then reloaded, with nothing checking that
     * the new shell could actually be fetched. Tap Update at the lodge and it
     * works; tap it on the 4th tee and the navigation times out, the service
     * worker's `caches.match('/')` fallback misses because the cache is gone,
     * and the app cannot be opened again until the phone is back in coverage.
     * The outbox is safe in localStorage but unreachable, which is worse than
     * a stale build.
     *
     * `skipWaiting` plus the reload is already sufficient — navigations are
     * network-first — so the nuke bought nothing and removed the only safety
     * net there was.
     */
  } catch {
    // Even with none of that, a plain reload usually lands the new build.
  }
  location.reload()
}
